import { Box, Button, CircularProgress, Divider, Menu, MenuItem, Popover, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { createState, downloadState, listStates, type SavedStateSummary } from '@api/stateApi';
import useNotificationStore from '@gui/store/notification';
import usePersistentStore from '@gui/store/persistent/persistentStore';
import { NotificationType, UUID } from '@visualizer/types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getSentence } from '@gui/constants/sentences';
import { COLUMN_WIDTH } from '../settings/SidePaneDialog';
import { PERSIST_STORE_VERSION, getCurrentBucketVersions, type PersistedBuckets } from '@gui/store/persistent/persistedBuckets';
import { loadPersistedStateIntoLocalStorage } from '@gui/store/persistent/loadPersistedState';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { useCapabilities } from '../../../../demo/capabilities';
import { getDisabledInEnvMessage, useEnvStore } from 'store/EnvStore';

const sanitizeResourceName = (resourceName: string): string => resourceName.replace(/\.json$/i, '');
const getDefaultStateName = (): string => {
    const now = new Date();
    const pad = (value: number) => value.toString().padStart(2, '0');
    return `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}_${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(
        now.getSeconds()
    )}`;
};

type SaveStateProps = {
    showContent: boolean;
};

type SavedStateOption = {
    id: string;
    name: string;
};

const State: React.FC<SaveStateProps> = ({ showContent }) => {
    const showNotification = useNotificationStore((s) => s.showNotification);
    const { canManageState } = useCapabilities();
    const env = useEnvStore((s) => s.env);
    const canUseApi = env !== 'local-offline';
    const [statesList, setStatesList] = useState<SavedStateOption[]>([]);
    const [statesLoading, setStatesLoading] = useState(false);
    const [savingState, setSavingState] = useState(false);
    const [stateMenuAnchor, setStateMenuAnchor] = useState<null | HTMLElement>(null);
    const [loadingStateName, setLoadingStateName] = useState<string | null>(null);
    const [confirmResetOpen, setConfirmResetOpen] = useState(false);
    const [savePopoverOpen, setSavePopoverOpen] = useState(false);
    const [saveStateName, setSaveStateName] = useState(getDefaultStateName());
    const [importPopoverOpen, setImportPopoverOpen] = useState(false);
    const [importText, setImportText] = useState('');
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importingState, setImportingState] = useState(false);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const resetButtonRef = useRef<HTMLButtonElement | null>(null);
    const importButtonRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        if (showContent) {
            setSaveStateName(getDefaultStateName());
        }
    }, [showContent]);

    const fetchStates = useCallback(async () => {
        if (!canUseApi) return;
        setStatesLoading(true);
        try {
            const savedStates = await listStates();
            const mapped = savedStates
                .map((state: SavedStateSummary) => ({
                    id: state.id,
                    name: sanitizeResourceName(state.name ?? ''),
                }))
                .filter((state) => state.name && state.name !== 'AutomaticSave' && state.name !== 'AutomaticState');
            mapped.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
            setStatesList(mapped);
        } catch (error) {
            console.error(error);
            showNotification('Error contacting server', NotificationType.Error);
        } finally {
            setStatesLoading(false);
        }
    }, [canUseApi, showNotification]);

    const handleReset = useCallback(() => {
        void ControllerManager.getInstance()
            .getStateController()
            .deleteAutomaticState()
            .finally(() => {
                window.location.reload();
            });
    }, []);

    const handleSaveState = useCallback(
        async (desiredName: string) => {
            if (!canUseApi) return;
            setSavingState(true);
            try {
                const storageName = usePersistentStore.persist.getOptions().name;
                if (!storageName) throw new Error('Cannot access local storage');

                const { global, layers, designSchemes } = usePersistentStore.getState();
                const state: PersistedBuckets = { global, layers, designSchemes };
                const serializedState = JSON.stringify({
                    version: PERSIST_STORE_VERSION,
                    bucketVersions: getCurrentBucketVersions(),
                    state,
                });

                const normalizedName = sanitizeResourceName(desiredName.trim()) || getDefaultStateName();
                await createState(normalizedName, JSON.parse(serializedState));
                showNotification('State saved successfully.', NotificationType.Success);
                await fetchStates();
            } catch (error) {
                console.error(error);
                showNotification('Error contacting server', NotificationType.Error);
            } finally {
                setSavingState(false);
            }
        },
        [canUseApi, fetchStates, showNotification]
    );

    const handleOpenLoadMenu = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            setStateMenuAnchor(event.currentTarget);
            void fetchStates();
        },
        [fetchStates]
    );

    const handleCloseLoadMenu = useCallback(() => {
        setStateMenuAnchor(null);
    }, []);

    const loadState = async (serializerState: string) => {
        const storageName = usePersistentStore.persist.getOptions().name!;
        try {
            loadPersistedStateIntoLocalStorage(serializerState, storageName);
            usePersistentStore.persist.rehydrate();
            showNotification('State loaded successfully.', NotificationType.Success);
        } catch (error) {
            console.error(error);
            showNotification('Failed to load the state', NotificationType.Error);
        }
    };

    const readFileAsText = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result ?? ''));
            reader.onerror = () => reject(reader.error);
            reader.readAsText(file);
        });

    const handleImportLoad = useCallback(async () => {
        setImportingState(true);
        try {
            let serialized = '';

            if (importFile) {
                serialized = await readFileAsText(importFile);
            } else {
                serialized = importText;
            }

            if (!serialized || !serialized.trim()) {
                showNotification('Please paste a JSON state or select a .json file.', NotificationType.Warning);
                return;
            }

            const trimmed = serialized.trim();
            const normalized = trimmed.startsWith('{') || trimmed.startsWith('[') ? trimmed : trimmed;

            await loadState(normalized);
            setImportPopoverOpen(false);
            setImportText('');
            setImportFile(null);
        } catch (error) {
            console.error(error);
            showNotification('Failed to import the state', NotificationType.Error);
        } finally {
            setImportingState(false);
        }
    }, [importFile, importText, showNotification]);

    const handleImportPaste = useCallback((event: React.ClipboardEvent<HTMLElement>) => {
        const files = event.clipboardData?.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (file) {
                setImportFile(file);
                event.preventDefault();
            }
        }
    }, []);

    const handleLoadState = useCallback(
        async (stateId: string, stateName: string) => {
            handleCloseLoadMenu();
            setLoadingStateName(stateName);
            try {
                const data = await downloadState(stateId as UUID);
                const serialized = typeof data === 'string' ? data : JSON.stringify(data);
                loadState(serialized);
            } catch (error) {
                console.error(error);
                showNotification('Error contacting server', NotificationType.Error);
            } finally {
                setLoadingStateName(null);
            }
        },
        [handleCloseLoadMenu, showNotification]
    );

    const handleLoadAutomaticState = useCallback(async () => {
        handleCloseLoadMenu();
        setLoadingStateName('AutomaticSave');
        try {
            const restored = await ControllerManager.getInstance()
                .getStateController()
                .restoreAutoSaveState(usePersistentStore.persist.getOptions().name);
            if (!restored) {
                throw new Error('Automatic save state is not available');
            }
            usePersistentStore.persist.rehydrate();
            showNotification('State loaded successfully.', NotificationType.Success);
        } catch (error) {
            console.error(error);
            showNotification('Error contacting server', NotificationType.Error);
        } finally {
            setLoadingStateName(null);
        }
    }, [handleCloseLoadMenu, showNotification]);

    const handleTextFieldKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        event.stopPropagation();
    }, []);

    const handleTextFieldKeyUp = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        event.stopPropagation();
    }, []);

    const loadMenuOpen = Boolean(stateMenuAnchor);
    const hasSavedStates = statesList.length > 0;

    if (!showContent) return null;

    return (
        <>
            <Box ref={panelRef} sx={{ width: '100%' }}>
                <Stack spacing={1}>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {getSentence('Settings.States.Tooltip')}
                    </Typography>
                    <Stack direction="row" spacing={1} style={{ marginBottom: '5px', marginTop: '20px' }}>
                        <Tooltip title={!canManageState ? getDisabledInEnvMessage(env) : ''}>
                            <span>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => {
                                        setSaveStateName(getDefaultStateName());
                                        setSavePopoverOpen(true);
                                    }}
                                    disabled={savingState || !canUseApi || !canManageState}
                                >
                                    <Box component="span" className="state-btn-content">
                                        <span className="state-btn-label" style={{ visibility: savingState ? 'hidden' : 'visible' }}>
                                            Save state
                                        </span>
                                        <span className="state-btn-spinner" style={{ opacity: savingState ? 1 : 0 }}>
                                            <CircularProgress size={12} color="inherit" />
                                        </span>
                                    </Box>
                                </Button>
                            </span>
                        </Tooltip>

                        <Tooltip title={!canManageState ? getDisabledInEnvMessage(env) : ''}>
                            <span>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleOpenLoadMenu}
                                    disabled={!canUseApi || !canManageState}
                                >
                                    <Box component="span" className="state-btn-content">
                                        <span className="state-btn-label" style={{ visibility: statesLoading ? 'hidden' : 'visible' }}>
                                            Load state
                                        </span>
                                        <span className="state-btn-spinner" style={{ opacity: statesLoading ? 1 : 0 }}>
                                            <CircularProgress size={12} color="inherit" />
                                        </span>
                                    </Box>
                                </Button>
                            </span>
                        </Tooltip>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => setConfirmResetOpen(true)}
                            ref={resetButtonRef}
                        >
                            Reset State
                        </Button>
                    </Stack>
                    {import.meta.env.MODE === 'development' && (
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => setImportPopoverOpen(true)}
                            ref={importButtonRef}
                            disabled={importingState}
                        >
                            {importingState ? <CircularProgress size={16} color="inherit" /> : 'Import state'}
                        </Button>
                    )}
                </Stack>
            </Box>

            <Menu anchorEl={stateMenuAnchor} open={loadMenuOpen} onClose={handleCloseLoadMenu} keepMounted>
                <MenuItem onClick={() => void handleLoadAutomaticState()} disabled={loadingStateName !== null}>
                    {loadingStateName === 'AutomaticSave' ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                    Automatic Save
                </MenuItem>
                <Divider />
                {statesLoading && !hasSavedStates ? (
                    <MenuItem disabled>
                        <CircularProgress size={16} sx={{ mr: 1 }} /> Loading...
                    </MenuItem>
                ) : hasSavedStates ? (
                    statesList.map((state) => (
                        <MenuItem key={state.id} onClick={() => handleLoadState(state.id, state.name)} disabled={loadingStateName !== null}>
                            {loadingStateName === state.name ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                            {state.name}
                        </MenuItem>
                    ))
                ) : (
                    <MenuItem disabled>No saved states found</MenuItem>
                )}
            </Menu>
            <Popover
                open={importPopoverOpen}
                onClose={() => setImportPopoverOpen(false)}
                anchorEl={panelRef.current}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{
                    sx: {
                        p: 2,
                        width: `${COLUMN_WIDTH - 32}px`,
                        maxWidth: `${COLUMN_WIDTH - 32}px`,
                        boxShadow: 4,
                    },
                }}
            >
                <Stack spacing={2}>
                    <Typography variant="body2">Paste a saved state JSON here or choose a .json file, then click Load.</Typography>

                    <Button variant="outlined" component="label" size="small">
                        Choose .json file
                        <input
                            hidden
                            type="file"
                            accept="application/json,.json"
                            onChange={(e) => {
                                const file = e.target.files && e.target.files.length ? e.target.files[0] : null;
                                setImportFile(file);
                            }}
                        />
                    </Button>

                    {importFile ? <Typography variant="caption">Selected file: {importFile.name}</Typography> : null}

                    <TextField
                        fullWidth
                        label="State JSON"
                        value={importText}
                        onChange={(e) => setImportText(e.target.value)}
                        onKeyDown={handleTextFieldKeyDown}
                        onKeyUp={handleTextFieldKeyUp}
                        onPaste={handleImportPaste}
                        size="small"
                        multiline
                        minRows={6}
                        placeholder="{ ... }"
                    />

                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                            variant="text"
                            onClick={() => {
                                setImportPopoverOpen(false);
                                setImportText('');
                                setImportFile(null);
                            }}
                            disabled={importingState}
                        >
                            Cancel
                        </Button>
                        <Button variant="contained" color="primary" onClick={() => void handleImportLoad()} disabled={importingState}>
                            Load
                        </Button>
                    </Stack>
                </Stack>
            </Popover>
            <Popover
                open={savePopoverOpen}
                onClose={() => setSavePopoverOpen(false)}
                anchorEl={panelRef.current}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{
                    sx: {
                        p: 2,
                        width: `${COLUMN_WIDTH - 32}px`,
                        maxWidth: `${COLUMN_WIDTH - 32}px`,
                        boxShadow: 4,
                    },
                }}
            >
                <Stack spacing={2}>
                    <TextField
                        fullWidth
                        label="State name"
                        value={saveStateName}
                        onChange={(event) => setSaveStateName(event.target.value)}
                        onKeyDown={handleTextFieldKeyDown}
                        onKeyUp={handleTextFieldKeyUp}
                        size="small"
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button variant="text" onClick={() => setSavePopoverOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={savingState || !saveStateName.trim() || !canUseApi}
                            onClick={() => {
                                setSavePopoverOpen(false);
                                void handleSaveState(saveStateName);
                            }}
                        >
                            Save
                        </Button>
                    </Stack>
                </Stack>
            </Popover>
            <Popover
                open={confirmResetOpen}
                onClose={() => setConfirmResetOpen(false)}
                anchorEl={panelRef.current}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{
                    sx: {
                        p: 2,
                        width: `${COLUMN_WIDTH - 32}px`,
                        maxWidth: `${COLUMN_WIDTH - 32}px`,
                        boxShadow: 4,
                    },
                }}
            >
                <Typography variant="body1" sx={{ mb: 2 }}>
                    This will reset all GUI values to defaults and reload the page, do you want to continue?
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button variant="text" onClick={() => setConfirmResetOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setConfirmResetOpen(false);
                            handleReset();
                        }}
                    >
                        Ok
                    </Button>
                </Stack>
            </Popover>
        </>
    );
};

export default State;
